import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from 'apollo-react/components/IconButton';
import StatusCheck from 'apollo-react/icons/StatusCheck';
import Trash from 'apollo-react/icons/Trash';
import Typography from '../../../../StyledTypography';
import styles from './FileList.module.css';

/**
 * Formats file size into human-readable string
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
const formatFileSize = (bytes) => {
  if (!bytes || Number.isNaN(bytes)) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let i = 0;
  while (parseFloat(size.toFixed(1)) >= 1000 && i < units.length - 1) {
    size /= 1000;
    i++;
  }
  return `${parseFloat(size.toFixed(1))} ${units[i]}`;
};

/**
 * FileListItem - Individual file item component
 * Renders a single file with name, size, and delete button
 *
 * @param {Object} props
 * @param {Object} props.file - File object with name, size, type, etc.
 * @param {boolean} props.isPendingDeletion - Whether file is marked for deletion
 * @param {boolean} props.disabled - Whether delete button is disabled
 * @param {Function} props.onDelete - Callback when delete is clicked
 */
const FileListItem = ({ file, isPendingDeletion, disabled, onDelete }) => {
  const { t } = useTranslation();

  const handleDelete = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !isPendingDeletion) {
        onDelete(file);
      }
    },
    [file, disabled, isPendingDeletion, onDelete]
  );

  const fullName =
    file.name || file.filename || t('fieldValueRenderer.unknownFile');
  let baseName = fullName;
  let extension = '';
  if (fullName.includes('.')) {
    const split = fullName.split('.');
    baseName = split.slice(0, -1).join('.');
    extension = `.${split[split.length - 1]}`;
  }
  const fileSize = formatFileSize(file.size || 0);

  return (
    <div
      className={`${styles.fileItem} ${isPendingDeletion ? styles.pendingDeletion : ''}`}
    >
      <div className={styles.fileInfo}>
        <Typography
          variant="bodySmall"
          className={`${styles.fileName} ${isPendingDeletion ? styles.strikethrough : ''}`}
        >
          <span className={styles.fileNameText}>{baseName}</span>
          <span className={styles.fileExtension}>{extension}</span>
          <span className={styles.fileSize}> ({fileSize})</span>
        </Typography>
      </div>
      {!isPendingDeletion && <StatusCheck className={styles.statusCheck} />}
      <IconButton
        size="small"
        onClick={handleDelete}
        disabled={disabled || isPendingDeletion}
        title={
          isPendingDeletion
            ? t('fileUpload.pendingDeletion', 'Pending deletion')
            : t('fileUpload.deleteFile', 'Delete file')
        }
        className={styles.deleteButton}
      >
        <Trash />
      </IconButton>
    </div>
  );
};

/**
 * FileList - Custom file list component with strikethrough support
 *
 * Displays a list of files with:
 * - File name and size
 * - Delete button
 * - Strikethrough styling for files pending deletion
 *
 * This component is used alongside Apollo FileUpload to properly display
 * server files, staged files, and local files with custom metadata.
 *
 * @param {Object} props
 * @param {Array} props.files - Array of file objects to display
 * @param {Set} props.pendingDeletionIds - Set of file IDs that are pending deletion
 * @param {boolean} props.disabled - Whether all delete buttons are disabled
 * @param {Function} props.onDelete - Callback when a file delete is requested
 */
const FileList = ({
  files = [],
  pendingDeletionIds = new Set(),
  disabled = false,
  onDelete,
}) => {
  if (!files || files.length === 0) {
    return null;
  }

  return (
    <div className={styles.fileList}>
      {files.map((file) => {
        // Get the unique identifier for this file
        const fileId = file.serverId || file.stagedId || file.id || file.name;
        const isPendingDeletion = pendingDeletionIds.has(fileId);

        return (
          <FileListItem
            key={fileId}
            file={file}
            isPendingDeletion={isPendingDeletion}
            disabled={disabled}
            onDelete={onDelete}
          />
        );
      })}
    </div>
  );
};

export default FileList;
